import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useState, useRef } from "react";
import Navigation from "./Navigation";
import Footer from "./Footer";
import AuthErrorScreen from "./AuthErrorScreen";
import { reportAuthFailure } from "../utils/sentry";
import { persistAuthError } from "../utils/authErrors";

const ADMIN_STATUS_CACHE_KEY = "admin_status_cache";
const CACHE_DURATION = 5 * 60 * 1000;
const NO_ADMIN_REASON =
  "Your account is signed in but does not have the admin claim required for this app.";

const AdminRoute = ({ children }) => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    user: auth0User,
    error: auth0SdkError,
    logout,
  } = useAuth0();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminCheckError, setAdminCheckError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const checkInProgress = useRef(false);
  const reportedAuthErrorRef = useRef(null);

  const retryAdminCheck = useCallback(() => {
    sessionStorage.removeItem(ADMIN_STATUS_CACHE_KEY);
    setAdminCheckError(null);
    setCheckingAdmin(true);
    checkInProgress.current = false;
    setRetryCount((count) => count + 1);
  }, []);

  useEffect(() => {
    const checkAdminClaim = async () => {
      if (checkInProgress.current) return;
      if (isLoading) return;

      if (!isAuthenticated) {
        sessionStorage.removeItem(ADMIN_STATUS_CACHE_KEY);
        setCheckingAdmin(false);
        setIsAdmin(false);
        setAdminCheckError(null);
        return;
      }

      try {
        const cached = sessionStorage.getItem(ADMIN_STATUS_CACHE_KEY);
        if (cached) {
          const { userId, isAdmin: cachedAdmin, timestamp } = JSON.parse(cached);
          if (userId === auth0User?.sub && Date.now() - timestamp < CACHE_DURATION) {
            setIsAdmin(cachedAdmin);
            setCheckingAdmin(false);
            return;
          }
        }
      } catch {
        // invalid cache
      }

      checkInProgress.current = true;

      try {
        const claimsPromise = getIdTokenClaims();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Admin check timeout")), 5000)
        );
        const claims = await Promise.race([claimsPromise, timeoutPromise]);
        const adminClaim = claims?.["https://brytebridge.com/penn-station/claims/is_admin"];
        const adminStatus = !!adminClaim;

        setIsAdmin(adminStatus);
        setAdminCheckError(null);

        if (auth0User?.sub) {
          sessionStorage.setItem(
            ADMIN_STATUS_CACHE_KEY,
            JSON.stringify({
              userId: auth0User.sub,
              isAdmin: adminStatus,
              timestamp: Date.now(),
            })
          );
        }
      } catch (error) {
        setIsAdmin(false);
        setAdminCheckError(
          error?.message || "Could not verify admin permissions. Please try again."
        );
        sessionStorage.removeItem(ADMIN_STATUS_CACHE_KEY);
      } finally {
        setCheckingAdmin(false);
        checkInProgress.current = false;
      }
    };

    checkAdminClaim();
  }, [isAuthenticated, isLoading, getIdTokenClaims, auth0User, retryCount]);

  const sessionError =
    auth0SdkError?.message ||
    (!isAuthenticated && !isLoading && !checkingAdmin
      ? "Your session expired or you are not signed in."
      : null);

  const ready = !isLoading && !checkingAdmin;

  useEffect(() => {
    if (!ready) return;

    let message = null;
    let step = null;

    if (sessionError) {
      message = sessionError;
      step = "session";
    } else if (adminCheckError) {
      message = adminCheckError;
      step = "admin_route_check";
    } else if (isAuthenticated && !isAdmin) {
      message = NO_ADMIN_REASON;
      step = "admin_claim";
    }

    if (!message || reportedAuthErrorRef.current === message) return;
    reportedAuthErrorRef.current = message;
    persistAuthError(message, { source: step });
    reportAuthFailure(step, message, { userId: auth0User?.sub });
  }, [
    ready,
    sessionError,
    adminCheckError,
    isAuthenticated,
    isAdmin,
    auth0User?.sub,
  ]);

  if (isLoading || checkingAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
          mt: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {isLoading ? "Verifying authentication..." : "Checking permissions..."}
        </Typography>
      </Box>
    );
  }

  if (sessionError) {
    return (
      <AuthErrorScreen
        title="Authentication required"
        message={sessionError}
        primaryLabel="Sign in again"
        onPrimary={() => {
          persistAuthError(sessionError);
          navigate("/sign-in", { replace: true, state: { error: sessionError } });
        }}
      />
    );
  }

  if (adminCheckError) {
    return (
      <AuthErrorScreen
        title="Could not verify admin access"
        message={adminCheckError}
        primaryLabel="Retry"
        onPrimary={retryAdminCheck}
        secondaryLabel="Sign out and try again"
        onSecondary={() => {
          persistAuthError(adminCheckError);
          logout({
            logoutParams: { returnTo: `${window.location.origin}/sign-in` },
          });
        }}
      />
    );
  }

  if (isAuthenticated && !isAdmin) {
    return (
      <AuthErrorScreen
        title="Access denied"
        message={NO_ADMIN_REASON}
        primaryLabel="Sign out and try another account"
        onPrimary={() => {
          persistAuthError(NO_ADMIN_REASON);
          logout({
            logoutParams: { returnTo: `${window.location.origin}/sign-in` },
          });
        }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Navigation />
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {children}
        <Footer />
      </Box>
    </Box>
  );
};

export default AdminRoute;
